import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { API, TESTIMONIALS as PLACEHOLDER_TESTIMONIALS } from "../data/constants";
import { useCurrency } from "../contexts/CurrencyContext";

/**
 * Testimonials section. Pulls real testimonials from `/api/testimonials` (with
 * region filtering based on the visitor's geolocation). Falls back to the
 * placeholder constants if the API returns no rows yet — so the marketing
 * section never appears empty during initial admin onboarding.
 */
export const Testimonials = ({
  title = "Trusted by Local Businesses",
  subtitle = "Real owners. Real results. Real customers walking through their doors.",
  background = "bg-gray-light"
}) => {
  const { isUAE } = useCurrency();
  const [testimonials, setTestimonials] = useState(null); // null = loading
  const [usingPlaceholders, setUsingPlaceholders] = useState(false);

  useEffect(() => {
    const region = isUAE ? "UAE" : "US";
    let cancelled = false;
    axios
      .get(`${API}/testimonials`, { params: { region, featured_only: true } })
      .then(({ data }) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data);
          setUsingPlaceholders(false);
        } else {
          setTestimonials(PLACEHOLDER_TESTIMONIALS);
          setUsingPlaceholders(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setTestimonials(PLACEHOLDER_TESTIMONIALS);
        setUsingPlaceholders(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isUAE]);

  if (testimonials === null) return null; // Avoid layout shift during initial fetch

  return (
    <section className={`section-padding ${background}`} data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">{title}</h2>
          <p className="text-gray-medium max-w-2xl mx-auto text-lg">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <TestimonialCard key={t.id} t={t} />
          ))}
        </div>

        {usingPlaceholders && (
          <p className="text-center text-xs text-gray-medium/70 mt-8 italic">
            * Sample testimonials shown while real client quotes are being collected.
          </p>
        )}
      </div>
    </section>
  );
};

const TestimonialCard = ({ t }) => {
  // Both API rows and placeholder rows expose `quote` and `name`. Placeholders
  // have `metric` + `avatarColor`; API rows have `photo_url` + `logo_url`.
  const rating = t.rating || 5;
  const subtitle = [t.role, t.company].filter(Boolean).join(" · ");

  return (
    <div className="relative bg-white p-6 rounded-2xl shadow-card border border-gray-100 card-hover flex flex-col" data-testid={`testimonial-${t.id}`}>
      <Quote className="absolute top-4 right-4 text-orange-primary/15" size={48} strokeWidth={1.5} />
      <div className="flex items-center gap-1 mb-3">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} size={16} className="text-orange-primary fill-orange-primary" />
        ))}
      </div>
      <p className="text-gray-dark mb-6 leading-relaxed flex-1">"{t.quote}"</p>

      {t.metric && (
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 bg-orange-light rounded-full self-start">
          <span className="text-orange-primary font-bold text-sm">{t.metric}</span>
        </div>
      )}

      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        {t.photo_url ? (
          <img src={t.photo_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div
            className={`w-12 h-12 rounded-full bg-gradient-to-br ${t.avatarColor || "from-orange-primary to-orange-secondary"} flex items-center justify-center text-white font-bold`}
            aria-hidden="true"
          >
            {t.name.charAt(0)}
          </div>
        )}
        <div className="flex-1">
          <p className="font-display font-bold text-gray-dark">{t.name}</p>
          {subtitle && <p className="text-sm text-gray-medium">{subtitle}</p>}
        </div>
        {t.logo_url && (
          <img src={t.logo_url} alt={`${t.company} logo`} className="h-8 w-auto object-contain opacity-80" />
        )}
      </div>
    </div>
  );
};

export default Testimonials;
